import PropTypes from 'prop-types';
import { NavLink as RouterLink } from 'react-router-dom';
// @mui
import { Box, Link, ListItemText } from '@mui/material';
//
import Iconify from '../../Iconify';
import { ListItemStyle, ListItemTextStyle, ListItemIconStyle } from './style';
import { isExternalLink } from '..';
// hooks
import useLocales from '../../../hooks/useLocales';
import useAuth from "../../../hooks/useAuth";
import {usePaidFeature} from '../../paid-features/PaidFeatureUpgrade';

// ----------------------------------------------------------------------

NavItemRoot.propTypes = {
  active: PropTypes.bool,
  open: PropTypes.bool,
  isCollapse: PropTypes.bool,
  onOpen: PropTypes.func,
  item: PropTypes.shape({
    children: PropTypes.array,
    adminOnly: PropTypes.bool,
    icon: PropTypes.any,
    info: PropTypes.any,
    path: PropTypes.string,
    title: PropTypes.string,
    paidFeature: PropTypes.string,
  }),
};

export function NavItemRoot({ item, isCollapse, open = false, active, onOpen }) {
  const { title, path, icon, info, children, adminOnly } = item;
  const {workspace} = useAuth();
  const {translate} = useLocales();
  const {available, requireAccess, upgradeDialog} = usePaidFeature(item.paidFeature || 'enrichment');
  const locked = Boolean(item.paidFeature) && !available;

  if (adminOnly && workspace && !workspace.isAdmin) {
    return (<></>);
  }

  const renderContent = (
    <>
      {icon && <ListItemIconStyle>{icon}</ListItemIconStyle>}
      <ListItemTextStyle data-ph-capture="safe" disableTypography primary={translate(`nav.${title}`)} isCollapse={isCollapse} />
      {!isCollapse && (
        <>
          {locked && <Iconify icon="eva:lock-outline" width={16} sx={{ml: 1}}/>}
          {info && info}
          {children && <ArrowIcon open={open} />}
        </>
      )}
    </>
  );

  if (locked) return <>
    <ListItemStyle component="button" onClick={requireAccess} data-tut={title} aria-label={`${translate(`nav.${title}`)} — exclusivo para assinantes`} sx={{width: '100%'}}>
      {renderContent}
    </ListItemStyle>
    {upgradeDialog}
  </>;

  if (children) {
    return (
      <ListItemStyle onClick={onOpen} activeRoot={active} data-tut={title}>
        {renderContent}
      </ListItemStyle>
    );
  }

  return isExternalLink(path) ? (
    <ListItemStyle component={Link} href={path} target="_blank" rel="noopener" data-tut={title}>
      {renderContent}
    </ListItemStyle>
  ) : (
    <ListItemStyle component={RouterLink} to={path} activeRoot={active} data-tut={title}>
      {renderContent}
    </ListItemStyle>
  );
}

// ----------------------------------------------------------------------

NavItemSub.propTypes = {
  active: PropTypes.bool,
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  item: PropTypes.shape({
    children: PropTypes.array,
    adminOnly: PropTypes.bool,
    info: PropTypes.any,
    path: PropTypes.string,
    title: PropTypes.string,
  }),
};

export function NavItemSub({ item, open = false, active = false, onOpen }) {
  const { title, path, info, children, adminOnly } = item;
  const {workspace} = useAuth();
  const {translate} = useLocales();

  if (adminOnly && workspace && !workspace.isAdmin) {
    return (<></>);
  }

  const renderContent = (
    <>
      <DotIcon active={active} />
      <ListItemText data-ph-capture="safe" disableTypography primary={translate(`nav.${title}`)} />
      {info && info}
      {children && <ArrowIcon open={open} />}
    </>
  );

  if (children) {
    return (
      <ListItemStyle onClick={onOpen} activeSub={active} subItem>
        {renderContent}
      </ListItemStyle>
    );
  }

  return isExternalLink(path) ? (
    <ListItemStyle component={Link} href={path} target="_blank" rel="noopener" subItem>
      {renderContent}
    </ListItemStyle>
  ) : (
    <ListItemStyle component={RouterLink} to={path} activeSub={active} subItem>
      {renderContent}
    </ListItemStyle>
  );
}

// ----------------------------------------------------------------------

DotIcon.propTypes = {
  active: PropTypes.bool,
};

export function DotIcon({ active }) {
  return (
    <ListItemIconStyle>
      <Box
        component="span"
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: 'text.disabled',
          transition: (theme) =>
            theme.transitions.create('transform', {
              duration: theme.transitions.duration.shorter,
            }),
          ...(active && {
            transform: 'scale(2)',
            bgcolor: 'primary.main',
          }),
        }}
      />
    </ListItemIconStyle>
  );
}

// ----------------------------------------------------------------------

ArrowIcon.propTypes = {
  open: PropTypes.bool,
};

export function ArrowIcon({ open }) {
  return (
    <Iconify
      icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
      sx={{ width: 16, height: 16, ml: 1 }}
    />
  );
}
